import axios from 'axios';

const STATUS_MESSAGES = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'Requested resource was not found.',
  405: 'This action is not allowed for the requested endpoint.',
  408: 'The request timed out. Please retry.',
  409: 'Conflict detected. Please refresh and try again.',
  410: 'This resource is no longer available.',
  413: 'Request payload is too large.',
  415: 'Unsupported content type.',
  422: 'Some fields are invalid. Please review and retry.',
  429: 'Too many requests. Please wait and try again.',
  500: 'Server error occurred. Please try again later.',
  502: 'Bad gateway. Please try again later.',
  503: 'Service temporarily unavailable.',
  504: 'Gateway timeout. Please try again.',
};

const toMessage = (payload) => {
  if (!payload) return null;

  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
    if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
    if (typeof payload.title === 'string' && payload.title.trim()) return payload.title;
  }

  return null;
};

export const normalizeApiError = (error, fallbackMessage = 'Something went wrong.') => {
  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
    return {
      status: 499,
      code: 'REQUEST_CANCELED',
      message: 'Request was canceled.',
      details: null,
      navigationUrl: null,
      isNetworkError: false,
      isTimeout: false,
    };
  }

  if (error?.code === 'ECONNABORTED') {
    return {
      status: 408,
      code: 'REQUEST_TIMEOUT',
      message: 'Request timed out. Please retry.',
      details: null,
      navigationUrl: null,
      isNetworkError: false,
      isTimeout: true,
    };
  }

  if (!error?.response) {
    return {
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Unable to reach server. Check your internet connection and try again.',
      details: null,
      navigationUrl: null,
      isNetworkError: true,
      isTimeout: false,
    };
  }

  const { status, data } = error.response;
  const message = toMessage(data) || STATUS_MESSAGES[status] || fallbackMessage;
  const code = data?.code || data?.error || `HTTP_${status}`;

  return {
    status,
    code,
    message,
    details: data?.details || null,
    navigationUrl: data?.navigationUrl || null,
    isNetworkError: false,
    isTimeout: false,
  };
};

export const getApiErrorMessage = (error, fallbackMessage = 'Something went wrong.') => {
  if (!error) return fallbackMessage;

  if (error.normalizedError?.message) {
    return error.normalizedError.message;
  }

  const responseStatus = error.response?.status;
  const responseMessage = toMessage(error.response?.data);
  if (responseMessage) {
    return responseMessage;
  }

  if (responseStatus && STATUS_MESSAGES[responseStatus]) {
    return STATUS_MESSAGES[responseStatus];
  }

  if (typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};
